import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Modal, Checkbox, Typography } from "antd";

const { Paragraph } = Typography;

const BannerWrapper = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  background: #fff;
  border-top: 1px solid #d9d9d9;
  padding-top: 30px;
  padding-bottom: 30px;
  z-index: 1000;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: left;
    align-items: flex-start;
  }
`;

const CookieText = styled(Paragraph)`
  margin: 0;
  margin-left: 7rem;
  display: flex;
  @media (max-width: 768px) {
    flex-wrap: wrap;
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const Text = styled.p`
  margin-top: 0;
  margin-bottom: 0;
  font-size: 14px;
  text-align: left;
  color: #121212;
`;

const CookieLeft = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  margin-right: 7rem;
  @media (max-width: 768px) {
    flex-wrap: wrap;
    margin-right: 1rem;
    justify-content: left;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const COOKIE_PREF_KEY = "evendy_cookie_prefs";

type CookiePrefs = {
  analytics: boolean;
  marketing: boolean;
};

const defaultPrefs: CookiePrefs = {
  analytics: false,
  marketing: false,
};

const applyConsentSettings = (prefs: CookiePrefs) => {
  // Google Analytics/GTag
  if (typeof window.gtag !== "undefined") {
    window.gtag("consent", "update", {
      analytics_storage: prefs.analytics ? "granted" : "denied",
      ad_storage: prefs.marketing ? "granted" : "denied",
    });
  }

  // Mixpanel
  if (window.mixpanel) {
    if (prefs.analytics) {
      window.mixpanel.opt_in_tracking();
    } else {
      window.mixpanel.opt_out_tracking();
    }
  }

  // Hotjar
  if (window._hjSettings) {
    if (!prefs.analytics) {
      // Disable Hotjar tracking
      window.hj = () => undefined;
      window._hjSettings = { hjid: 0, hjsv: 0 };
    } else if (window._hjSettings.hjid) {
      // Reinitialize Hotjar if needed
      const hotjarScript = document.createElement("script");
      hotjarScript.async = true;
      hotjarScript.src = `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`;
      document.head.appendChild(hotjarScript);
    }
  }

  // Facebook Pixel
  if (typeof window.fbq !== "undefined" && !prefs.marketing) {
    window.fbq("consent", "revoke");
  }
};

const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(defaultPrefs);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_PREF_KEY);
    if (stored) {
      const savedPrefs = JSON.parse(stored) as CookiePrefs;
      setPrefs(savedPrefs);
      applyConsentSettings(savedPrefs);
    } else {
      setVisible(true);
      applyConsentSettings(defaultPrefs);
    }
  }, []);

  const savePrefs = (newPrefs: CookiePrefs) => {
    localStorage.setItem(COOKIE_PREF_KEY, JSON.stringify(newPrefs));
    applyConsentSettings(newPrefs);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    savePrefs({ analytics: true, marketing: true });
  };

  const handleSaveCustom = () => {
    savePrefs(prefs);
    setModalOpen(false);
  };

  const handleCheckboxChange = (key: keyof CookiePrefs) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {visible && (
        <BannerWrapper>
          <CookieText>
            <Text>We use cookies to personalize content and analyze traffic. You can manage your preferences.</Text>
          </CookieText>
          <CookieLeft>
            <Button type="link" onClick={() => setModalOpen(true)}>
              Manage Preferences
            </Button>
            <Button onClick={() => savePrefs(defaultPrefs)}>Reject All</Button>
            <Button type="primary" onClick={handleAcceptAll}>
              Accept All
            </Button>
          </CookieLeft>
        </BannerWrapper>
      )}

      <Modal
        open={modalOpen}
        title="Manage Cookie Preferences"
        onCancel={() => setModalOpen(false)}
        onOk={handleSaveCustom}
        okText="Save Preferences"
      >
        <Checkbox checked={prefs.analytics} onChange={() => handleCheckboxChange("analytics")}>
          Allow analytics cookies (Hotjar, Amplitude, Mixpanel)
        </Checkbox>
        <br />
        <Checkbox checked={prefs.marketing} onChange={() => handleCheckboxChange("marketing")}>
          Allow marketing cookies (Google Ads, Facebook)
        </Checkbox>
      </Modal>
    </>
  );
};

export default CookieBanner;
